import { useState, useEffect, useRef } from "react";
import { Room, RoomEvent, Track } from "livekit-client";
import { useLocation } from "react-router-dom";
import left from '../assets/left.png';
import { UpdateUserLimits, UpdateAnalytics, GetStreamingLimit } from '../Firebase';

const LiveStreaming = () => {
  const location = useLocation();
  const { voiceID, avatarID } = location.state || {};

  // Refs
  const mediaElementRef = useRef(null);
  const roomRef = useRef(null);
  const destroyedRef = useRef(false);
  const pendingTracksRef = useRef([]);
  const keepAliveIntervalRef = useRef(null);
  const keepAliveStartedRef = useRef(false);

  // Spoken-time accounting (server-sourced remaining)
  const remainingRef = useRef(0);        // remaining seconds (from Firebase)
  const usedSpokenRef = useRef(0);       // seconds spoken in THIS session (for UI only now)
  const postSpeechEnforcementTimerRef = useRef(null);
  const enforcementShownRef = useRef(false); // prevent repeated alert

  // Session
  const [sessionInfo, setSessionInfo] = useState(null);
  const [sessionID, setSessionID] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [sessionStarted, setSessionStarted] = useState(false);

  // UI
  const [taskInput, setTaskInput] = useState("");
  const [buttonLoader, setButtonLoader] = useState(false);
  const [msg, setMsg] = useState('You have reached your limit');
  const [isAlertOpen, setAlertOpen] = useState(false);
  const [loading, setLoading] = useState(true); // ✅ show loader immediately
  const [needsUserGesture, setNeedsUserGesture] = useState(false);
  const [phone, setPhone] = useState("");
  const [remainingUI, setRemainingUI] = useState(0); // mirrors remainingRef for display
  const [videoVisible, setVideoVisible] = useState(false); // ✅ hide video frame until media added

  const API_CONFIG = {
    apiKey: "Njc0NDNkNzg3ZTczNDA3YjkwYTI5ZGU3ZmZiNzNiNDMtMTcxOTQ4Mjk5Mg==",
    serverUrl: "https://api.heygen.com",
  };

  // ---- Keep Alive ----
  const startKeepAlive = () => {
    if (keepAliveStartedRef.current || !sessionInfo?.session_id || !sessionToken) return;
    keepAliveStartedRef.current = true;
    keepAliveIntervalRef.current = setInterval(async () => {
      try {
        await fetch(`${API_CONFIG.serverUrl}/v1/streaming.keep_alive`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${sessionToken}`,
          },
          body: JSON.stringify({ session_id: sessionInfo.session_id }),
        });
      } catch {}
    }, 40000);
  };
  const stopKeepAlive = () => {
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
    keepAliveStartedRef.current = false;
  };

  // ---- Attach media ----
  const attachTrackToVideo = (track) => {
    const videoEl = mediaElementRef.current;
    if (!videoEl) {
      pendingTracksRef.current.push(track);
      return;
    }
    if (!videoEl.srcObject) videoEl.srcObject = new MediaStream();
    videoEl.srcObject.addTrack(track.mediaStreamTrack);

    // media added → reveal video, stop loader
    setVideoVisible(true);
    setLoading(false);

    startKeepAlive();

    videoEl.play()
      .then(() => setNeedsUserGesture(false))
      .catch(() => setNeedsUserGesture(true));
  };

  // Flush queued tracks once ref is set
  useEffect(() => {
    if (mediaElementRef.current && pendingTracksRef.current.length) {
      const q = [...pendingTracksRef.current];
      pendingTracksRef.current = [];
      q.forEach(attachTrackToVideo);
    }
  }, [mediaElementRef.current]);

  // ---- Session stop & cleanup ----
  const stopSession = async () => {
    try {
      if (!sessionID) return;
      const response = await fetch(`${API_CONFIG.serverUrl}/v1/streaming.stop`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": API_CONFIG.apiKey,
        },
        body: JSON.stringify({ session_id: sessionID })
      });
      await response.json().catch(() => ({}));
    } catch {}
  };

  const cleanup = async () => {
    if (destroyedRef.current) return;
    destroyedRef.current = true;

    stopKeepAlive();

    if (postSpeechEnforcementTimerRef.current) {
      clearTimeout(postSpeechEnforcementTimerRef.current);
      postSpeechEnforcementTimerRef.current = null;
    }

    try {
      if (roomRef.current) {
        await roomRef.current.disconnect();
        roomRef.current = null;
      }
    } catch {}

    await stopSession();

    if (mediaElementRef.current?.srcObject instanceof MediaStream) {
      mediaElementRef.current.srcObject.getTracks().forEach(t => { try { t.stop(); } catch {} });
      mediaElementRef.current.srcObject = null;
    }
  };

  // ---- Boot: fetch server limit + init session ----
  useEffect(() => {
    // loader is already true at mount
    const userData = JSON.parse(sessionStorage.getItem("AI_UserData"));
    if (userData?.phone) setPhone(userData.phone);

    const initializeSession = async () => {
      try {
        // 1) Token
        const tokenRes = await fetch(`${API_CONFIG.serverUrl}/v1/streaming.create_token`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": API_CONFIG.apiKey,
          },
        });
        const tokenJson = await tokenRes.json();
        const token = tokenJson?.data?.token;
        setSessionToken(token);

        // 2) New session
        const newRes = await fetch(`${API_CONFIG.serverUrl}/v1/streaming.new`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({
            version: "v2",
            quality: "medium",
            avatar_name: avatarID,
            voice: { voice_id: voiceID, rate: 1 },
            video_encoding: "H264",
            activity_idle_timeout: Math.max(remainingRef.current || 120, 120),
          }),
        });
        const newJson = await newRes.json();
        const _sessionInfo = newJson?.data;
        setSessionInfo(_sessionInfo);
        setSessionID(_sessionInfo?.session_id);

        // 3) Start
        const startRes = await fetch(`${API_CONFIG.serverUrl}/v1/streaming.start`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ session_id: _sessionInfo?.session_id }),
        });
        await startRes.json();
        setSessionStarted(true);

        // 4) LiveKit
        const lkRoom = new Room();
        roomRef.current = lkRoom;

        lkRoom.on(RoomEvent.TrackSubscribed, (track) => {
          if (track.kind === Track.Kind.Video || track.kind === Track.Kind.Audio) {
            attachTrackToVideo(track);
          }
        });

        await lkRoom.connect(_sessionInfo?.url, _sessionInfo?.access_token);
        // keep loader ON until we actually attach first track
      } catch (error) {
        setLoading(false);
        setAlertOpen(true);
        setMsg('Voice not supported for this Avatar');
      }
    };

    const boot = async () => {
      // 0) Fetch remaining from Firebase first
      try {
        const freshRemaining = await GetStreamingLimit(userData?.phone);
        remainingRef.current = Number(freshRemaining || 0);
        setRemainingUI(remainingRef.current);
      } catch {
        remainingRef.current = 0;
        setRemainingUI(0);
      }

      if (remainingRef.current <= 0) {
        setAlertOpen(true);
        setMsg("You have reached your streaming limit");
        setLoading(false);
        return;
      }

      try { await UpdateAnalytics("streaming"); } catch {}
      await initializeSession();
    };

    boot();
    return () => { cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Handlers ----
  const handleUserInteraction = async () => {
    if (!mediaElementRef.current) return;
    try {
      await mediaElementRef.current.play();
      setNeedsUserGesture(false);
    } catch {}
  };

  const sendText = async () => {
    if (!sessionInfo || !sessionStarted || !taskInput.trim()) return;
    if (remainingRef.current <= 0) {
      setAlertOpen(true);
      setMsg("No remaining speaking time. Please top up.");
      return;
    }

    try {
      setButtonLoader(true);
      const res = await fetch(`${API_CONFIG.serverUrl}/v1/streaming.task`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({
          session_id: sessionInfo.session_id,
          text: taskInput,
          task_type: "talk",
        }),
      });
      const json = await res.json();

      const ms = json?.data?.duration_ms ?? 0;
      const sec = Math.ceil(ms / 1000);

      // Update local accounting immediately
      usedSpokenRef.current += sec;
      remainingRef.current -= sec;
      setRemainingUI(remainingRef.current);

      // Allow the avatar to finish this utterance, then COMMIT to Firebase
      if (postSpeechEnforcementTimerRef.current) {
        clearTimeout(postSpeechEnforcementTimerRef.current);
      }
      postSpeechEnforcementTimerRef.current = setTimeout(async () => {
        try {
          // Deduct ONLY this utterance now (incremental commit)
          await UpdateUserLimits("streaming_limit", phone, sec);

          // Refresh remaining from sessionStorage (UpdateUserLimits syncs it)
          const s = JSON.parse(sessionStorage.getItem("AI_UserData") || "{}");
          if (typeof s.streaming_limit === "number") {
            remainingRef.current = s.streaming_limit;
            setRemainingUI(remainingRef.current);
          }
        } catch {}

        // If we are now at/below zero, stop session and inform the user (no extra deduction here)
        if (remainingRef.current <= 0 && !enforcementShownRef.current) {
          enforcementShownRef.current = true;
          try { await stopSession(); } catch {}
          setAlertOpen(true);
          setMsg("You have reached your streaming limit");
        }
      }, ms || 0);

      setTaskInput("");
    } catch {
      // ignore
    } finally {
      setButtonLoader(false);
    }
  };

  async function exitStreaming() {
    // ❗ No deduction here anymore (we already commit per utterance).
    await cleanup();
    window.history.back();
  }

  return (
    <div className="d-flex flex-column justify-content-between" style={{ height: '100vh', position: 'relative' }}>
      <div className='container pt-4 p-0'>
        <div className='back-button' onClick={exitStreaming}>
          <img src={left} alt="Back" />
          <h1 className='m-0'>Live Streaming</h1>
        </div>
      </div>

      {/* VIDEO ALWAYS MOUNTED (unmuted) */}
      <div className="container" style={{ position: 'relative' }}>
        <div className='row'>
          <div className='col-12'>
            <video
              ref={mediaElementRef}
              className="w-100 max-h-[400px] border rounded-lg my-5"
              playsInline
              onClick={handleUserInteraction}
              style={{ visibility: videoVisible ? "visible" : "hidden" }} // ✅ hide frame until media added
            />
            {/* Loading overlay — shown immediately on mount, hidden when first media attaches */}
            {loading && !videoVisible && (
              <div
                style={{
                  position: 'absolute', inset: 0, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(0,0,0,0.25)',
                  pointerEvents: 'none' // clicks pass through if needed
                }}
              >
                <span className="Spinloader"></span>
              </div>
            )}
            {/* Tap-to-start overlay if autoplay blocked (since not muted) */}
            {needsUserGesture && videoVisible && !loading && (
              <div
                onClick={handleUserInteraction}
                style={{
                  position: 'absolute', inset: 0, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(0,0,0,0.35)', color: 'white',
                  cursor: 'pointer', fontWeight: 600
                }}
              >
                Tap to start audio
              </div>
            )}
          </div>
        </div>
      </div>

      {isAlertOpen && (
        <div className="container">
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <h3 className="m-0">{msg}</h3>
            <button type="button" className="btn-close" aria-label="Close" onClick={() => setAlertOpen(false)}></button>
          </div>
        </div>
      )}

      <div className="container">
        <div className="d-flex flex-column">
          <h2>Prompt</h2>
          <input
            type="text"
            value={taskInput}
            onChange={(e) => setTaskInput(e.target.value)}
            placeholder="Enter text for avatar to speak"
            className="flex-1 min-w-[200px] p-2 border border-gray-300 rounded-md"
          />
          <button
            onClick={sendText}
            className={`main-button mt-2 mb-2 ${buttonLoader ? 'opacity-60 cursor-not-allowed' : ''}`}
            disabled={buttonLoader}
          >
            {buttonLoader ? "Sending ..." : "Send"}
          </button>
          <div className="text-muted" style={{ fontSize: 12 }}>
            Remaining speaking time: {Math.max(remainingUI, 0)}s — Used this session: {usedSpokenRef.current}s
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveStreaming;
